import { useState, useEffect } from 'react';
import { singletonHook } from 'react-singleton-hook';
import { media, MediaEventType, Vendor } from '../../webrtc/media';
import { toast, VideoIndicator } from '@doxyme/ui/components';
import { useIntl } from '@doxyme/locale';
import { useTrackHighCpu } from '../useTracking';
import { useCanFlipCamera } from '../useCanFlipCamera';

export type UseLocalMediaReturnType = {
  videoElement: HTMLElement | undefined;
  screenElement: HTMLElement | undefined;
  hasVideo: boolean;
  hasAudio: boolean;
  hasScreenAudio: boolean;
  tracks: MediaStreamTrack[];
  videoTrack?: MediaStreamTrack;
  audioTrack?: MediaStreamTrack;
  vendor?: Vendor;
  isRearCameraUsed: boolean;
  isAudioFallbackActive: boolean;
  isVideoDisableWarningActive: boolean;
  /**
   * If true if the user explicitly grants camera access. False could indicate:
   * - permission is undecided (`undefined`, do not set isVideoAccessBlocked);
   * - access is denied by user (`MediaAccessStatus.Denied`);
   * - an error occurred (`MediaAccessStatus.Error`).
   *
   * hasVideoAccess could not be equated with isVideoAccessBlocked, as false alone is inconclusive.
   */
  hasVideoAccess: boolean;
  hasAudioAccess: boolean;
  /**
   * True if video/audio access is blocked due to user denial, system policy, or an error
   */
  isVideoAccessBlocked: boolean;
  isAudioAccessBlocked: boolean;
  /**
   * True when mic publishing is enabled but the OS has muted the capture device.
   */
  isAudioMutedBySystem: boolean;
  canFlipCamera: boolean;
};

export const hookInitValues = {
  videoElement: undefined,
  screenElement: undefined,
  hasVideo: false,
  hasAudio: false,
  hasVideoAccess: false,
  hasAudioAccess: false,
  hasScreenAudio: false,
  tracks: [],
  videoTrack: undefined,
  audioTrack: undefined,
  vendor: undefined,
  isRearCameraUsed: false,
  isAudioFallbackActive: false,
  isVideoDisableWarningActive: false,
  isVideoAccessBlocked: false,
  isAudioAccessBlocked: false,
  isAudioMutedBySystem: false,
  canFlipCamera: false,
};

export const useLocalMediaImpl = (): UseLocalMediaReturnType => {
  const [videoElement, setVideoElement] = useState<HTMLElement | undefined>(
    media.videoElement,
  );

  const [screenElement, setScreenElement] = useState<HTMLElement | undefined>(
    media.screenElement,
  );
  const [hasScreenAudio, setScreenAudio] = useState<boolean>(
    media.hasScreenAudio,
  );

  const [hasVideo, setHasVideo] = useState<boolean>(media.hasVideo);
  const [hasAudio, setHasAudio] = useState<boolean>(media.hasAudio);
  const [hasVideoAccess, setHasVideoAccess] = useState<boolean>(
    media.hasVideoAccess,
  );
  const [hasAudioAccess, setHasAudioAccess] = useState<boolean>(
    media.hasAudioAccess,
  );
  const [isAudioFallbackActive, setIsAudioFallbackActive] =
    useState<boolean>(false);
  const [isVideoDisableWarningActive, setIsVideoDisableWarningActive] =
    useState<boolean>(false);

  const [isVideoAccessBlocked, setIsVideoAccessBlocked] = useState<boolean>(
    media.isVideoAccessBlocked,
  );
  const [isAudioAccessBlocked, setIsAudioAccessBlocked] = useState<boolean>(
    media.isAudioAccessBlocked,
  );
  const [isAudioMutedBySystem, setIsAudioMutedBySystem] = useState<boolean>(
    media.isAudioMutedBySystem,
  );
  const canFlipCamera = useCanFlipCamera();
  const { addHighCpuReason, removeHighCpuReason } = useTrackHighCpu();

  const [tracks, setTracks] = useState(media.tracks);
  const videoTrack = tracks.find((track) => track.kind === 'video');
  const audioTrack = tracks.find((track) => track.kind === 'audio');

  const { formatMessage } = useIntl();

  useEffect(() => {
    const unsubscribes = [
      media.on([MediaEventType.VideoElementUpdated], () => {
        setVideoElement(media.videoElement);
      }),
      media.on([MediaEventType.MediaStatusChange], (): void => {
        setHasVideo(media.hasVideo);
        setHasAudio(media.hasAudio);
        setIsAudioMutedBySystem(media.isAudioMutedBySystem);
      }),
      media.on(
        [
          MediaEventType.MediaStreamAudioTrackUpdated,
          MediaEventType.MediaStreamVideoTrackUpdated,
        ],
        (): void => {
          setTracks(media.tracks);
          setHasVideo(media.hasVideo);
          setHasAudio(media.hasAudio);
          setIsAudioMutedBySystem(media.isAudioMutedBySystem);
        },
      ),
      media.on([MediaEventType.MediaStreamScreenTrackUpdated], (): void => {
        setScreenElement(
          media.screenElement === null ? undefined : media.screenElement,
        );
        setScreenAudio(media.hasScreenAudio);
      }),

      media.on(
        [MediaEventType.MediaAccessStatusChange, MediaEventType.DevicesChange],
        (): void => {
          setHasVideoAccess(media.hasVideoAccess);
          setHasAudioAccess(media.hasAudioAccess);
          setIsVideoAccessBlocked(media.isVideoAccessBlocked);
          setIsAudioAccessBlocked(media.isAudioAccessBlocked);
        },
      ),
      media.on([MediaEventType.MediaAudioFallbackEnabled], (): void => {
        setIsAudioFallbackActive(true);
        toast({
          title: formatMessage({ id: 'audioFallback.publisher.disable.toast' }),
          icon: <VideoIndicator size='small' hasVideo={false} />,
        });
      }),
      media.on([MediaEventType.MediaAudioFallbackDisabled], (): void => {
        setIsAudioFallbackActive(false);
      }),
      media.on([MediaEventType.MediaVideoDisableWarningEnabled], (): void => {
        setIsVideoDisableWarningActive(true);

        toast.warning({
          title: formatMessage({ id: 'audioFallback.publisher.warning.toast' }),
        });
      }),
      media.on([MediaEventType.MediaVideoDisableWarningDisabled], (): void => {
        setIsVideoDisableWarningActive(false);
      }),
    ];

    return () => {
      for (const unsubscribe of unsubscribes) {
        unsubscribe();
      }
    };
  }, [media]);

  useEffect(() => {
    if (media.isVirtualBackgroundEnabled) {
      addHighCpuReason('virtual background');
    } else {
      removeHighCpuReason('virtual background');
    }
  }, [addHighCpuReason, removeHighCpuReason, media.isVirtualBackgroundEnabled]);

  return {
    videoElement,
    screenElement,
    hasScreenAudio,
    hasVideo,
    hasAudio,
    hasVideoAccess,
    hasAudioAccess,
    isAudioFallbackActive,
    isVideoDisableWarningActive,
    videoTrack,
    audioTrack,
    tracks,
    vendor: Vendor.Vonage,
    isRearCameraUsed: media.isRearCameraUsed,
    isVideoAccessBlocked,
    isAudioAccessBlocked,
    isAudioMutedBySystem,
    canFlipCamera,
  };
};

export const useLocalMedia = singletonHook<UseLocalMediaReturnType>(
  hookInitValues,
  useLocalMediaImpl,
);
